import React, { useState, useEffect } from "react";
import { 
  Search, ExternalLink, Bell, BellOff, TrendingUp, 
  Calendar, AlertCircle, ChevronLeft, ChevronRight as ChevronRightIcon, 
  Crown, Loader2, X, Trash2, Tag, Clock, Sparkles
} from "lucide-react";
import { useTranslation } from '../../contexts/LanguageContext';
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../App";

const TrackedMerchantsList = ({ onToggleNotification }) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const { httpClient, validateToken, user } = useAuth();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [trackedMerchants, setTrackedMerchants] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);
  const [currentTogglingId, setCurrentTogglingId] = useState(null);
  const [trackedCount, setTrackedCount] = useState(0);
  const [isPremium, setIsPremium] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [merchantToToggle, setMerchantToToggle] = useState(null);
  const [currentToggleStatus, setCurrentToggleStatus] = useState(null);
  const [activePromosData, setActivePromosData] = useState({});
  const [loadingPromos, setLoadingPromos] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  
  const itemsPerPage = 5;

  // Update current time every second for countdown timers
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Fetch tracked merchants, count, and premium status
  useEffect(() => {
    fetchTrackedData();
  }, []);

  useEffect(() => {
    // Fetch promos for all tracked merchants when the list changes
    if (trackedMerchants.length > 0) {
      fetchAllMerchantPromos();
    }
  }, [trackedMerchants]);

  const fetchTrackedData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (!httpClient) {
        console.error("httpClient not available");
        setIsLoading(false);
        return;
      }

      // Fetch premium status first
      await fetchPremiumStatus();
      
      // Then fetch tracked merchants and count in parallel
      await Promise.all([
        fetchTrackedMerchants(),
        fetchTrackedCount()
      ]);
    } catch (error) {
      console.error('Error fetching tracked data:', error);
      setError(t('trackedMerchants.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPremiumStatus = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/premium-status');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchPremiumStatus();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setIsPremium(data.isPremium);
    } catch (error) {
      console.error('Error fetching premium status:', error);
      setIsPremium(false);
    }
  };

  const fetchTrackedMerchants = async () => {
    try {
      if (!httpClient) return;
      
      console.log('Fetching tracked merchants via JWT');
      
      const response = await httpClient.request('/api/user-tracked-merchants');
  
      console.log('Response status:', response.status);
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchTrackedMerchants();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Tracked merchants received:', data);
      setTrackedMerchants(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching tracked merchants:', error);
      setTrackedMerchants([]);
    }
  };

  const fetchAllMerchantPromos = async () => {
    try {
      const promosData = {};
      const loadingStates = {};
      
      // Set loading state for each merchant
      trackedMerchants.forEach(merchant => {
        if (merchant.id) {
          loadingStates[merchant.id] = true;
        }
      });
      setLoadingPromos(loadingStates);

      // Fetch promos for each merchant in parallel
      const promoPromises = trackedMerchants.map(async (merchant) => {
        if (!merchant.id || !httpClient) return null;
        
        try {
          const response = await httpClient.request(`/api/merchant-promos?merchant_id=${merchant.id}`);
          
          if (!response.ok) {
            if (response.status === 401) {
              const revalidated = await validateToken();
              if (!revalidated) {
                navigate("/demo");
                return null;
              }
              // Return empty array for failed requests after retry
              return { merchantId: merchant.id, promos: [] };
            }
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }
          
          const data = await response.json();
          return { merchantId: merchant.id, promos: data };
        } catch (error) {
          console.error(`Error fetching promos for merchant ${merchant.id}:`, error);
          return { merchantId: merchant.id, promos: [] };
        }
      });

      const results = await Promise.all(promoPromises);
      
      // Process results
      results.forEach(result => {
        if (result) {
          promosData[result.merchantId] = result.promos;
        }
      });

      setActivePromosData(promosData);
      
      // Clear loading states
      const clearedLoadingStates = {};
      Object.keys(loadingStates).forEach(key => {
        clearedLoadingStates[key] = false;
      });
      setLoadingPromos(clearedLoadingStates);
    } catch (error) {
      console.error('Error fetching all merchant promos:', error);
    }
  };

  const fetchTrackedCount = async () => {
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/user/tracked-merchants/count');

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return fetchTrackedCount();
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTrackedCount(data.count || 0);
    } catch (error) {
      console.error('Error fetching tracked count:', error);
      setTrackedCount(0);
    }
  };

  // Calculate tracking limit
  const maxTracked = isPremium ? Infinity : 5;
  const canTrackMore = isPremium || trackedCount < maxTracked;

  // Helper function to extract values from MongoDB format
  const extractValue = (value) => {
    if (value && typeof value === 'object') {
      if ('$numberInt' in value) return parseInt(value.$numberInt, 10);
      if ('$numberDouble' in value) return parseFloat(value.$numberDouble);
      if ('$numberLong' in value) return parseInt(value.$numberLong, 10);
      if ('$date' in value) return new Date(parseInt(value.$date.$numberLong, 10));
      if ('$oid' in value) return value.$oid;
    }
    return value;
  };

  // Process offers to extract values from MongoDB format
  const processOffers = (offers) => {
    if (!offers || !Array.isArray(offers)) return [];
    
    return offers.map(offer => ({
      ...offer,
      plan_id: offer.plan_id ? extractValue(offer.plan_id) : null,
      promo_price: offer.promo_price ? extractValue(offer.promo_price) : null,
      discount_percent: offer.discount_percent ? extractValue(offer.discount_percent) : null
    }));
  };

  // Get promos for a specific merchant
  const getMerchantPromos = (merchantId) => {
    return activePromosData[merchantId] || [];
  };

  // Get all active promos count for a merchant
  const getActivePromosCount = (merchantId) => {
    const promos = getMerchantPromos(merchantId);
    return promos.length;
  };

  // Check if merchant has active promos
  const hasActivePromos = (merchantId) => {
    return getActivePromosCount(merchantId) > 0;
  };

  // Helper function to calculate countdown timer
  const getCountdownTimer = (endDate) => {
    const date = extractValue(endDate);
    const end = new Date(date);
    const diffTime = end - currentTime;
    
    if (diffTime <= 0) {
      return { text: t('trackedMerchants.promoExpired'), urgent: false };
    }

    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((diffTime % (1000 * 60)) / 1000);

    // If more than 7 days, show the date
    if (diffDays > 7) {
      return { 
        text: `${t('trackedMerchants.validUntil')} ${formatDate(end)}`, 
        urgent: false 
      };
    }

    // Show countdown timer for less than 7 days
    let timerText = '';
    if (language === 'it') {
      if (diffDays > 0) {
        timerText = `${diffDays}g ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    } else {
      if (diffDays > 0) {
        timerText = `${diffDays}d ${diffHours}h ${diffMinutes}m`;
      } else if (diffHours > 0) {
        timerText = `${diffHours}h ${diffMinutes}m ${diffSeconds}s`;
      } else {
        timerText = `${diffMinutes}m ${diffSeconds}s`;
      }
    }

    return { 
      text: timerText, 
      urgent: diffDays <= 3 
    };
  };

  // Get the most urgent promo for display
  const getMostUrgentPromo = (merchantId) => {
    const promos = getMerchantPromos(merchantId);
    if (promos.length === 0) return null;

    let mostUrgent = null;
    let minTime = Infinity;

    promos.forEach(promo => {
      const endDate = extractValue(promo.end_date);
      const end = new Date(endDate);
      const diffTime = end - currentTime;
      
      if (diffTime > 0 && diffTime < minTime) {
        minTime = diffTime;
        mostUrgent = promo;
      }
    });

    return mostUrgent;
  };

  // Filter and sort merchants - active promos first
  const filteredMerchants = trackedMerchants
    .filter(merchant => 
      merchant && (
        (merchant.name && merchant.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (merchant.category && merchant.category.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    )
    .sort((a, b) => {
      // Merchants with active promos come first
      const aHasPromos = hasActivePromos(a.id);
      const bHasPromos = hasActivePromos(b.id);
      
      if (aHasPromos && !bHasPromos) return -1;
      if (!aHasPromos && bHasPromos) return 1;
      
      // If both have or don't have promos, sort by name
      return (a.name || '').localeCompare(b.name || '');
    });

  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentMerchants = filteredMerchants.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Navigate to merchant page (when logo is clicked)
  const handleViewMerchant = (merchantId) => {
    if (merchantId) {
      navigate(`/merchant/${merchantId}`);
    }
  };

  // Handle external link click (website / affiliate)
  const handleMerchantWebsiteClick = (merchant) => {
    if (!merchant) return;
    
    const url = merchant.affiliate_link || merchant.website;
    if (!url) {
      alert(t('trackedMerchants.noWebsite') || 'No website available');
      return;
    }
    
    // Confirmation dialog
    if (window.confirm(t('trackedMerchants.redirectConfirm') || 'You are about to leave the app and visit the merchant website. Continue?')) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleToggleNotification = (merchantId, currentStatus) => {
    // If turning ON notifications, proceed directly
    if (!currentStatus) {
      performToggleNotification(merchantId, currentStatus);
    } else {
      // If turning OFF notifications, show confirmation modal
      const merchant = trackedMerchants.find(m => m.id === merchantId);
      setMerchantToToggle({
        id: merchantId,
        name: merchant?.name || t('trackedMerchants.thisMerchant')
      });
      setCurrentToggleStatus(currentStatus);
      setShowConfirmModal(true);
    }
  };

  const performToggleNotification = async (merchantId, currentStatus) => {
    setIsToggling(true);
    setCurrentTogglingId(merchantId);
    
    try {
      if (!httpClient) {
        alert(t('trackedMerchants.httpClientError'));
        return;
      }

      const response = await httpClient.request('/api/toggle-promo-notifications', {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          merchant_id: merchantId,
          enabled: !currentStatus
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          // Retry the request
          return performToggleNotification(merchantId, currentStatus);
        }
        const data = await response.json();
        if (data.limitReached) {
          alert(data.error || t('trackedMerchants.limitReached'));
          return;
        }
        throw new Error(data.error || t('trackedMerchants.updateFailed'));
      }

      const data = await response.json();
      
      if (data.success) {
        // Refresh the tracked merchants list
        await fetchTrackedData();
        
        if (onToggleNotification) {
          onToggleNotification(merchantId, !currentStatus, data.trackedCount);
        }
      } else {
        alert(data.error || t('trackedMerchants.updateFailed'));
      }
    } catch (error) {
      console.error('Error toggling notification:', error);
      alert(t('trackedMerchants.updateFailed'));
    } finally {
      setIsToggling(false);
      setCurrentTogglingId(null);
      setShowConfirmModal(false);
      setMerchantToToggle(null);
      setCurrentToggleStatus(null);
    }
  };

  const handleConfirmToggle = () => {
    if (merchantToToggle && currentToggleStatus !== null) {
      performToggleNotification(merchantToToggle.id, currentToggleStatus);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('common.na');
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'it' ? 'it-IT' : 'en-GB', { 
        day: 'numeric', 
        month: 'short'
      });
    } catch (error) {
      return t('common.na');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#253985]" />
        <span className="ml-2 text-gray-600">{t('trackedMerchants.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 bg-gradient-to-br from-[#253985]/10 to-[#1a2a6d]/10 rounded-xl border border-[#253985]/20">
        <AlertCircle className="h-12 w-12 text-[#253985] mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-800 mb-2">{t('trackedMerchants.errorLoadingData')}</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={fetchTrackedData}
          className="bg-[#253985] hover:bg-[#1a2a6d] text-white px-4 py-2 rounded-lg transition-colors"
        >
          {t('common.tryAgain')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-8">
      {/* Tracking Limit Info */}
      {!isPremium && (
        <div className="bg-[#253985]/10 p-4 rounded-lg border border-[#253985]/20">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#253985]/10 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-[#253985]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-[#253985] font-medium">
                  {t('trackedMerchants.tracking')} {trackedCount}/{maxTracked} {t('trackedMerchants.merchants')}
                </p>
                <p className="text-xs text-[#253985]/80">
                  {canTrackMore 
                    ? `${maxTracked - trackedCount} ${t('trackedMerchants.slotsRemaining')}`
                    : t('trackedMerchants.limitReached')
                  }
                </p>
              </div>
            </div>
            {!canTrackMore && (
              <button
                onClick={() => navigate("/upgrade")}
                className="flex items-center gap-2 bg-gradient-to-r from-[#253985] to-amber-500 text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:shadow-lg hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              >
                <Crown className="h-4 w-4" />
                {t('common.upgrade')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Header with search */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between p-1">
        <div className="relative flex-1 max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder={t('trackedMerchants.searchPlaceholder')}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#253985] focus:border-[#253985]"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </div>
        <div className="text-sm text-gray-600">
          {filteredMerchants.length} {t('trackedMerchants.merchants')}
        </div>
      </div>

      {/* Active Promos Section */}
      {filteredMerchants.some(m => hasActivePromos(m.id)) && (
        <div className="bg-gradient-to-r from-[#253985]/10 to-[#1a2a6d]/10 border border-[#253985]/20 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-full bg-[#253985]/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-[#253985]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#253985]">{t('trackedMerchants.activePromotions')}</h3>
              <p className="text-sm text-[#253985]/80">
                {filteredMerchants.filter(m => hasActivePromos(m.id)).length} {t('trackedMerchants.haveActivePromos')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tracked Merchants Cards */}
      <div className="space-y-3">
        {currentMerchants.length > 0 ? (
          currentMerchants.map((merchant) => {
            const hasActivePromos = getActivePromosCount(merchant.id) > 0;
            const mostUrgentPromo = getMostUrgentPromo(merchant.id);
            const loadingPromo = loadingPromos[merchant.id];
            const hasWebsite = !!(merchant.affiliate_link || merchant.website);
            
            return (
              <div 
                key={merchant.id} 
                className={`flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors group ${
                  hasActivePromos 
                    ? 'border-[#253985] bg-[#253985]/5 shadow-sm' 
                    : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  {/* Logo Section - now clickable */}
                  <div className="relative cursor-pointer" onClick={() => handleViewMerchant(merchant.id)}>
                    <div className={`w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center border shadow-sm ${
                      hasActivePromos 
                        ? 'border-[#253985]/30 bg-white' 
                        : 'border-gray-200 bg-white'
                    } group-hover:border-[#253985]/50 transition-colors`}>
                      {merchant.logo_url ? (
                        <img
                          src={merchant.logo_url}
                          alt={merchant.name}
                          className="w-10 h-10 object-contain transition-transform duration-300 group-hover:scale-110"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI4IiBmaWxsPSJub25lIiBzdHJva2U9IiMyNTM5ODUiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMyA5bDktNyA5IDd2MTFhMiAyIDAgMCAxLTIgMkg1YTIgMiAwIDAgMS0yLTJ6Ij48L3BhdGg+PHBvbHlsaW5lIHBvaW50cz0iOSAyMiA5IDEyIDE1IDEyIDE1IDIyIj48L3BvbHlsaW5lPjwvc3ZnPg==';
                          }}
                        />
                      ) : (
                        <div className="text-[#253985]/50 group-hover:text-[#253985] transition-colors">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                          </svg>
                        </div>
                      )}
                    </div>
                    {hasActivePromos && (
                      <div className="absolute -top-1.5 -left-1.5 w-5 h-5 bg-red-500 rounded-full border-2 border-white shadow-sm flex items-center justify-center">
                        <Sparkles className="h-2.5 w-2.5 text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h4 className={`font-medium ${
                        hasActivePromos ? 'text-[#253985]' : 'text-gray-900'
                      }`}>
                        {merchant.name || t('trackedMerchants.unknownMerchant')}
                      </h4>
                      <div className="flex items-center gap-2">
                        {hasActivePromos && (
                          <>
                            <span className="text-xs bg-[#253985]/10 text-[#253985] px-2 py-1 rounded-full flex items-center">
                              <Tag className="h-3 w-3 mr-1" />
                              {getActivePromosCount(merchant.id)} {t('trackedMerchants.activePromos')}
                            </span>
                            {hasActivePromos && mostUrgentPromo && (
                              <span className={`text-xs px-2 py-1 rounded-full flex items-center ${
                                getCountdownTimer(mostUrgentPromo.end_date).urgent 
                                  ? 'bg-red-100 text-red-800' 
                                  : 'bg-[#253985]/10 text-[#253985]'
                              }`}>
                                <Clock className={`h-3 w-3 mr-1 ${
                                  getCountdownTimer(mostUrgentPromo.end_date).urgent 
                                    ? 'text-red-600' 
                                    : 'text-[#253985]'
                                }`} />
                                {getCountdownTimer(mostUrgentPromo.end_date).text}
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Promo Information - Simplified */}
                    {loadingPromo ? (
                      <div className="flex items-center gap-2 mt-2">
                        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
                        <span className="text-xs text-gray-500">{t('trackedMerchants.checkingPromos')}</span>
                      </div>
                    ) : !hasActivePromos && (
                      <div className="mt-2 flex items-center gap-4 text-sm text-gray-600">
                        {(merchant.active_promos || 0) > 0 ? (
                          <div className="flex items-center gap-1">
                            <TrendingUp className="h-4 w-4 text-[#253985]" />
                            <span className="font-medium text-[#253985]">
                              {merchant.active_promos} {t('trackedMerchants.activePromos')}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-500">
                            {t('trackedMerchants.noActivePromos')}
                          </span>
                        )}
                        
                        {merchant.last_promo_date && (
                          <div className="flex items-center gap-1">
                            <Calendar className="h-4 w-4 text-[#253985]" />
                            <span>
                              {t('trackedMerchants.lastPromo')} {formatDate(merchant.last_promo_date)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Right side buttons */}
                <div className="flex items-center gap-4 ml-6">
                  {/* Spacing between info and bell button (already handled by gap) */}
                  <button
                    onClick={() => handleToggleNotification(merchant.id, merchant.enabled)}
                    disabled={isToggling && currentTogglingId === merchant.id}
                    className={`p-2 rounded-lg transition-colors ${
                      merchant.enabled 
                        ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    } ${(isToggling && currentTogglingId === merchant.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={merchant.enabled 
                      ? t('trackedMerchants.notificationsOn') 
                      : t('trackedMerchants.notificationsOff')
                    }
                  >
                    {isToggling && currentTogglingId === merchant.id ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : merchant.enabled ? (
                      <Bell className="h-5 w-5" />
                    ) : (
                      <BellOff className="h-5 w-5" />
                    )}
                  </button>
                  
                  {/* External link button - replaces old "View Merchant" button */}
                  <button
                    onClick={() => handleMerchantWebsiteClick(merchant)}
                    disabled={!hasWebsite}
                    className={`p-2 rounded-lg flex items-center justify-center ${
                      hasWebsite 
                        ? 'bg-[#253985] text-white hover:bg-[#1a2a6d]' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                    title={hasWebsite 
                      ? t('trackedMerchants.visitWebsite') 
                      : t('trackedMerchants.noWebsite')
                    }
                  >
                    <ExternalLink className="h-5 w-5" />
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-12 bg-gradient-to-br from-[#253985]/10 to-[#1a2a6d]/10 rounded-xl border border-[#253985]/20">
            <div className="mx-auto w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-inner mb-4">
              <AlertCircle className="h-10 w-10 text-[#253985]" />
            </div>
            <p className="text-gray-600 text-lg mb-2">
              {searchTerm 
                ? t('trackedMerchants.noSearchResults') 
                : t('trackedMerchants.noTrackedMerchants')
              }
            </p>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              {t('trackedMerchants.exploreMarketplace')}
            </p>
            <button
              onClick={() => navigate("/marketplace")}
              className="bg-[#253985] hover:bg-[#1a2a6d] text-white px-6 py-3 rounded-lg transition-colors"
            >
              {t('trackedMerchants.browseMerchants')}
            </button>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {filteredMerchants.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-6 px-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
            }`}
          >
            <ChevronLeft className="h-5 w-5 mr-2" />
            {t('common.previous')}
          </button>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                    currentPage === pageNum 
                      ? 'bg-white text-[#253985] border-2 border-[#253985] font-bold shadow-sm' 
                      : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`flex items-center px-4 py-2 rounded-lg transition-all ${
              currentPage === totalPages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-[#253985] text-white hover:bg-[#1a2a6d] shadow-md hover:shadow-lg'
            }`}
          >
            {t('common.next')}
            <ChevronRightIcon className="h-5 w-5 ml-2" />
          </button>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full shadow-2xl transform transition-all duration-300">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {t('trackedMerchants.confirmRemoval')}
              </h3>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setMerchantToToggle(null);
                  setCurrentToggleStatus(null);
                }}
                className="text-gray-400 hover:text-gray-500 p-1"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="mb-6">
              <p className="text-gray-600">
                {t('trackedMerchants.confirmRemoveMessage')} <span className="font-medium text-[#253985]">{merchantToToggle?.name}</span>?
              </p>
              <p className="text-gray-500 text-sm mt-2">
                {t('trackedMerchants.willNoLongerNotify')}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setMerchantToToggle(null);
                  setCurrentToggleStatus(null);
                }}
                className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleConfirmToggle}
                className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-600 transition-all shadow-md hover:shadow-lg flex items-center"
              >
                <BellOff className="h-5 w-5 mr-2" />
                {t('trackedMerchants.stopTracking')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackedMerchantsList;
